import type { ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppLayout } from './componentesCompartilhados/layout/AppLayout'
import { UsuarioProvider, useUsuario } from './contexts/UsuarioContext'
import { PendenciasSocketProvider } from './contexts/PendenciasSocketContext'
import { PendenciaListener } from './componentesCompartilhados/pendencias/PendenciaListener'
import { AdminRoute } from './componentesCompartilhados/routing/AdminRoute'
import Login from './auth/Login'
import Dashboard from './dashboard/Dashboard'
import { Projetos } from './dominios/projetos'
import Faturamento from './dominios/projetos/pages/Faturamento'
import FaturamentoAcompanhamento from './dominios/projetos/pages/FaturamentoAcompanhamento'
import Kanban from './dominios/projetos/pages/Kanban'
import { Usuarios } from './dominios/usuarios'
import { Pessoas } from './dominios/pessoas'
import { Relatorios } from './dominios/relatorios'
import { Suportes } from './dominios/suportes'
import GeracaoEnvio from './dominios/suportes/pages/GeracaoEnvio'
import { Times, PessoasTimes, ConfiguracaoTimes } from './dominios/times'
import UploadPlanilhaSuporte from './dominios/faturamento/pages/UploadPlanilhaSuporte'
import LancamentoManual from './dominios/projetos/pages/LancamentoManual'
import Menus from './dominios/menus/pages/Menus'
import Permissoes from './dominios/menus/pages/Permissoes'
import { PaginaDefault } from './componentesCompartilhados/PaginaDefault'
import ConsultaNfMomentum from './features/consultaNfMomentum/pages/ConsultaNfMomentum'
import ErrosSistema from './features/errosSistema/pages/ErrosSistema'
import Premiacao from './dominios/premiacao/pages/Premiacao'
import Performance from './dominios/performance/pages/Performance'
import { PaginaJobs } from './dominios/jobs'
import { BiMovidesk, BiProjetos } from './dominios/bis'
import {
  AjudaCustoRegistro,
  AjudaCustoManutencao,
  AjudaCustoRelatorio,
  AjudaCustoParametrizacao,
} from './dominios/ajudaCusto'

function RedirectPreserveQuery({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useUsuario()

  if (loading)
  {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-brand-500" />
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated)
    return <Navigate to="/login" replace />

  return <>{children}</>
}

// Rota exclusiva do papel Externo (Bônus Presencial > Registro)
const ROTA_EXTERNO = '/ajuda-de-custo/registro'

/**
 * Restringe o papel Externo (código '4') a acessar apenas a tela de Registro de
 * Ajuda de Custo (Bônus Presencial). Qualquer outra rota é redirecionada.
 */
function ExternoGuard({ children }: { children: ReactNode }) {
  const { usuario } = useUsuario()
  const { pathname } = useLocation()

  const isAdmin = usuario?.papeis?.includes('1') ?? false
  const isExterno = (usuario?.papeis?.includes('4') ?? false) && !isAdmin

  if (isExterno && pathname !== ROTA_EXTERNO)
    return <Navigate to={ROTA_EXTERNO} replace />

  return <>{children}</>
}

function AppRoutes() {
  const rotasAutenticadas = (
    <>
      <Route index element={<Dashboard />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="projetos" element={<Projetos />} />
      <Route path="projetos/faturamento" element={<RedirectPreserveQuery to="/faturamento/envio" />} />
      <Route
        path="projetos/kanban"
        element={(
          <div className="m-0 h-full w-full p-0">
            <Kanban />
          </div>
        )}
      />
      <Route path="faturamento/envio" element={<Faturamento />} />
      <Route path="faturamento/acompanhamento" element={<FaturamentoAcompanhamento />} />
      <Route
        path="faturamento/upload-planilha-suporte"
        element={(
          <AdminRoute>
            <UploadPlanilhaSuporte />
          </AdminRoute>
        )}
      />
      <Route
        path="faturamento/lancamento-manual"
        element={(
          <AdminRoute>
            <LancamentoManual />
          </AdminRoute>
        )}
      />
      <Route
        path="usuarios"
        element={(
          <AdminRoute>
            <Usuarios />
          </AdminRoute>
        )}
      />
      <Route
        path="pessoas"
        element={(
          <AdminRoute>
            <Pessoas />
          </AdminRoute>
        )}
      />
      <Route path="relatorios" element={<Relatorios />} />
      <Route path="suportes" element={<Suportes />} />
      <Route path="suportes/geracao-envio" element={<GeracaoEnvio />} />
      <Route
        path="menus"
        element={(
          <AdminRoute>
            <Menus />
          </AdminRoute>
        )}
      />
      <Route
        path="menus/permissoes"
        element={(
          <AdminRoute>
            <Permissoes />
          </AdminRoute>
        )}
      />
      <Route path="times" element={<Times />} />
      <Route path="times/pessoas-times" element={<PessoasTimes />} />
      <Route
        path="times/configuracao"
        element={(
          <AdminRoute>
            <ConfiguracaoTimes />
          </AdminRoute>
        )}
      />
      <Route
        path="configuracoes"
        element={(
          <div className="p-6">
            <h1 className="text-2xl font-bold">Configurações</h1>
            <p className="text-gray-600">Página em desenvolvimento</p>
          </div>
        )}
      />
      <Route
        path="consulta-nf-momentum"
        element={(
          <AdminRoute>
            <ConsultaNfMomentum />
          </AdminRoute>
        )}
      />
      <Route
        path="erros-sistema"
        element={(
          <AdminRoute>
            <ErrosSistema />
          </AdminRoute>
        )}
      />
      <Route path="premiacao" element={<Premiacao />} />
      <Route path="performance" element={<Performance />} />
      <Route
        path="jobs"
        element={(
          <AdminRoute>
            <PaginaJobs />
          </AdminRoute>
        )}
      />
      <Route path="ajuda-de-custo/registro" element={<AjudaCustoRegistro />} />
      <Route
        path="ajuda-de-custo/manutencao"
        element={(
          <AdminRoute>
            <AjudaCustoManutencao />
          </AdminRoute>
        )}
      />
      <Route
        path="ajuda-de-custo/relatorio"
        element={(
          <AdminRoute>
            <AjudaCustoRelatorio />
          </AdminRoute>
        )}
      />
      <Route
        path="ajuda-de-custo/parametrizacao"
        element={(
          <AdminRoute>
            <AjudaCustoParametrizacao />
          </AdminRoute>
        )}
      />
      <Route
        path="bis/projetos"
        element={(
          <AdminRoute>
            <BiProjetos />
          </AdminRoute>
        )}
      />
      <Route
        path="bis/movidesk"
        element={(
          <AdminRoute>
            <BiMovidesk />
          </AdminRoute>
        )}
      />
      <Route path="*" element={<PaginaDefault />} />
    </>
  )

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={(
          <ProtectedRoute>
            <ExternoGuard>
              <AppLayout />
            </ExternoGuard>
          </ProtectedRoute>
        )}
      >
        {rotasAutenticadas}
      </Route>
    </Routes>
  )
}

function App() {
  return (
    <UsuarioProvider>
      <AppWithSocket />
    </UsuarioProvider>
  )
}

function AppWithSocket() {
  const { usuario } = useUsuario()
  const usuarioId = usuario?.id

  return (
    <PendenciasSocketProvider usuarioId={usuarioId}>
      <PendenciaListener />
      <AppRoutes />
    </PendenciasSocketProvider>
  )
}

export default App
